'use client';

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FiDownload, FiFileText } from 'react-icons/fi';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';

import { importVehiclesFile } from '../../api-custom/vehicles-import-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { VehicleList } from './vehicle-list';

const TEMPLATE_FILE_URL = '/images/asista-vehicle-import-template.xlsx';
const IMPORT_ERROR_MESSAGE = 'Import sa nepodaril. Skúste to prosím znova.';

function getImportValidationMessage(error: ValidationError): string {
  const rawFields: unknown = error.fields;
  const payload =
    rawFields && typeof rawFields === 'object' && !Array.isArray(rawFields)
      ? (rawFields as Record<string, unknown>)
      : {};
  const fields =
    payload.errors && typeof payload.errors === 'object' && !Array.isArray(payload.errors)
      ? payload.errors
      : payload;
  const messages = Object.values(fields).flatMap(value =>
    Array.isArray(value)
      ? value.filter((message): message is string => typeof message === 'string' && !!message.trim())
      : [],
  );

  if (messages.length) return messages.join(' ');
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  if (error.message?.trim() && error.message !== ValidationError.statusText) return error.message;

  return IMPORT_ERROR_MESSAGE;
}

export function FleetImportContent() {
  const { t } = useTranslations();
  const authToken = useCustomerStore(s => s.me!.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportError(null);
    setFile(e.target.files?.[0] ?? null);
  };

  const handleImport = async () => {
    if (!file) return;

    setIsImporting(true);
    setImportError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploaded = await uploadFile({ authToken, formData, locale });

      await importVehiclesFile({ locale, authToken, fileId: uploaded.data.id });

      setFile(null);
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast({ title: 'Zoznam vozidiel bol úspešne naimportovaný.', variant: 'success' });
    } catch (e) {
      if (e instanceof ValidationError) {
        setImportError(getImportValidationMessage(e));
      } else {
        setImportError(IMPORT_ERROR_MESSAGE);
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className={cn('custom-container', 'flex w-full flex-col gap-8 pb-14 sm:pb-0')}>
      <p className="text-left text-sm text-muted-foreground">
        Nahrajte zoznam vozidiel vo vzorovom formáte a naimportujte ich hromadne do vášho vozového
        parku.
      </p>

      <div className="flex w-full max-w-[800px] flex-col gap-6 rounded-2xl border border-border bg-white p-6 sm:p-8">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">1. Stiahnite si vzorový zoznam</p>
            <p className="text-sm text-muted-foreground">
              Vyplňte údaje o vozidlách do predpripraveného Excel súboru.
            </p>
          </div>

          <Button asChild variant="outline" className="w-full sm:w-fit">
            <a href={TEMPLATE_FILE_URL} download="asista-vehicle-import-template.xlsx">
              <FiDownload size={18} /> Stiahnuť vzorový zoznam
            </a>
          </Button>
        </div>

        <div className="h-px w-full bg-border" />

        <div className="flex flex-col gap-3">
          <div>
            <p className="font-semibold">2. Nahrajte vyplnený zoznam</p>
            <p className="text-sm text-muted-foreground">Podporovaný formát: .xlsx</p>
          </div>

          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-border p-4 transition-colors hover:border-primary">
            <span className="flex items-center gap-2 truncate text-sm">
              <FiFileText size={18} className="shrink-0 text-muted-foreground" />
              {file ? file.name : t('text950')}
            </span>
            <input type="file" accept=".xlsx" className="hidden" onChange={handleFileChange} />
          </label>

          {importError && <p className="text-sm text-destructive-foreground">{importError}</p>}

          <Button
            onClick={handleImport}
            disabled={!file}
            isLoading={isImporting}
            className="w-full sm:w-fit"
          >
            Importovať
          </Button>
        </div>
      </div>

      <div className="flex w-full flex-col gap-4">
        <p className="font-semibold">Vaše vozidlá</p>
        <VehicleList />
      </div>
    </div>
  );
}
